import React, { useState, useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";
import { login } from "../../actions/authActions";
import Loader from "../../Common/Loader";
import Logo from "../../assets/loginlogo.png";
import api from "../../Utils/api";
import { useLoadingContext } from "../../Loading/LoadingContext";
import {encryptAndSetItemLocalStorage ,decryptAndGetItemLocalStorage } from "../../Utils/cryptosessionStorage";
import { showMessage } from "../../Utils/showMessageUtils";
import OtpInput from "../../Common/OtpInput";
import "./Login.css";

// The OTP lifetime the API enforces, mirrored here so the countdown can be
// drawn as a proportion rather than a bare number.
const OTP_SECONDS = 300;

const Login = () => {
  const [MobileNo, setMobileNo] = useState("");
  const [showOTPPart, setShowOTPPart] = useState(false);
  // One string, not six slots — the field is a single input now.
  const [OTP, setOTP] = useState("");
  const dispatch = useDispatch();
  const isLoading = useSelector((state) => state.auth.loading);
  const { setIsLoading } = useLoadingContext();
  const [countdown, setCountdown] = useState(300);
  const [showNewOTP, setShowNewOTP] = useState(false);
  const [MobilNoValidation, setMobilNoValidation] = useState(false);
  const [OTPValidation, setOTPValidation] = useState(false);
  // const isAuthenticated = isLoggedIn();

  useEffect(() => {
    let timer;
    if (showOTPPart && countdown > 0) {
      timer = setInterval(() => {
        setCountdown((prevCountdown) => prevCountdown - 1);
      }, 1000);
    } else if (countdown === 0) {
      showMessage(400,"OTP has expired. Please request a new OTP.");
      setShowNewOTP(true);
      setOTP("");
    }
    return () => clearInterval(timer);
  }, [showOTPPart, countdown]);

  const validateMobileNo = () => {
    if (!MobileNo || MobileNo.length !== 10) {
      setMobilNoValidation(true);
      return false;
    } else {
      setMobilNoValidation(false);
      return true;
    }
  };


  const validateOfOTP = (otpValue) => {

    if (!otpValue || otpValue.toString().length !== 6) {
      setOTPValidation(true);
      return false;
    } else {
      setOTPValidation(false);
      return true;
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (validateMobileNo()) {
      setIsLoading(true);
      try {
        const result = await api.post(
          `ClientLogin/CheckClientLogin?MobileNo=${MobileNo}`
        );
        showMessage(result.data.statusCode,result.data.message)
        if (result.data.statusCode === 200) {
          setShowOTPPart(true);
          encryptAndSetItemLocalStorage ("LoginMobileNo",result.data.data.mobileNo);
          encryptAndSetItemLocalStorage ("LoginUserEmail",result.data.data.emailIdTo);
          encryptAndSetItemLocalStorage ("OnePolicyID",result.data.data.policyencvalue)
          encryptAndSetItemLocalStorage ("_MNO_C",result.data.data.finalMobileNo);
          encryptAndSetItemLocalStorage ("_EI0D0C",result.data.data.finalEmailID);
        }
      } catch (error) {
        showMessage(400,"An error occurred")
      } finally {
        setIsLoading(false);
      }
    }
  };

  const handleFinalSubmit = async (e) => {
    if (countdown > 0) {
      /* Validated as the string the customer typed, then sent as the number the
         API has always been given. Checking the string is what makes a code
         beginning with a zero pass the length test — parseInt drops it. */
      if (validateOfOTP(OTP)) {
        dispatch(login({ OTP: parseInt(OTP, 10) }));
      }
    } else {
      showMessage(400,"OTP has expired. Please request a new OTP.");
    }
  };

  const handleGenerateOTP = async (e) => {
    let Nu = decryptAndGetItemLocalStorage ("_MNO_C")
    setIsLoading(true);
    try {
      const result = await api.post(
        `ClientLogin/GenerateNewOTP?MobileNo=${Nu}`
      );
      if (result.data.statusCode === 200) {
        setCountdown(300);
        setShowNewOTP(false);
      } else {
        showMessage(400,result.data.message);
      }
    } catch (error) {
      showMessage(400,"An error occurred");
    } finally {
      setIsLoading(false);
    }
  };

  const handleOTPChange = (digits) => {
    setOTP(digits);
    setOTPValidation(false);
  };

  // --- Presentation only -----------------------------------------------------
  const minutes = Math.floor(countdown / 60);
  const seconds = countdown % 60;
  const timeLeft = `${minutes}:${seconds < 10 ? "0" : ""}${seconds}`;
  const countdownPercent = Math.max(0, (countdown / OTP_SECONDS) * 100);
  // The same thresholds the dashboard uses to colour a policy nearing expiry,
  // so a customer only has to learn the language once.
  const timerTone =
    countdown <= 60 ? "lg__timer--urgent" : countdown <= 120 ? "lg__timer--warn" : "";

  return (
    <div className="lg">
      {/* Concentric arcs radiating from below the sheet — the guilloche that
          sits under the text on a printed policy document. */}
      <div className="lg__ground" aria-hidden="true"></div>

      <div className="lg__sheet">
        {/* THE COVERAGE RAIL. The validity bar every policy card in this app
            already uses, borrowed here to show where you are in the sequence.
            Two segments because there are genuinely two steps. */}
        <div className="lg__rail" aria-hidden="true">
          <span
            className={`lg__rail-seg ${
              showOTPPart ? "lg__rail-seg--done" : "lg__rail-seg--active"
            }`}
          ></span>
          <span
            className={`lg__rail-seg ${showOTPPart ? "lg__rail-seg--active" : ""}`}
          ></span>
        </div>

        <div className="lg__inner">
          <img className="lg__brand" src={Logo} alt="Maa Consultancy" />

          {isLoading && <Loader />}

          {showOTPPart === false && (
            <form className="lg__form" onSubmit={handleSubmit}>
              <div>
                <h1 className="lg__title">Sign in</h1>
                <p className="lg__sub">
                  Kindly enter your registered mobile number below. We'll send a
                  6-digit code to it.
                </p>
              </div>

              <div className={`ui-field ${MobilNoValidation ? "ui-field--invalid" : ""}`}>
                <label className="ui-field__label" htmlFor="mobileNo">
                  Mobile number
                </label>
                <div className="ui-field__control">
                  <span className="ui-field__prefix">+91</span>
                  <input
                    id="mobileNo"
                    className="ui-field__input"
                    /* tel, not number: a phone number is a string of digits,
                       not a quantity. It brings the right keypad, drops the
                       spinner arrows, and lets maxLength actually apply. */
                    type="tel"
                    inputMode="numeric"
                    autoComplete="tel"
                    maxLength={10}
                    name="MobileNo"
                    placeholder="10-digit number"
                    value={MobileNo}
                    onChange={(e) => setMobileNo(e.target.value)}
                    aria-invalid={MobilNoValidation}
                    aria-describedby={MobilNoValidation ? "mobileNo-error" : undefined}
                  />
                </div>
                {MobilNoValidation && (
                  <p className="ui-field__error" id="mobileNo-error" role="alert">
                    <i className="fa fa-circle-exclamation" aria-hidden="true"></i>
                    That number needs 10 digits. Check the number on your policy.
                  </p>
                )}
              </div>

              <button className="ui-btn ui-btn--primary ui-btn--block" type="submit">
                Send code
              </button>
            </form>
          )}

          {showOTPPart && (
            <form
              className="lg__form"
              onSubmit={(e) => {
                e.preventDefault();
                handleFinalSubmit(e);
              }}
            >
              <div>
                <h1 className="lg__title">Enter your code</h1>
                <p className="lg__sub">
                  Sent to <strong>+91 {MobileNo}</strong> and to the email on your
                  policy.
                </p>
              </div>

              <div className={`ui-field ${OTPValidation ? "ui-field--invalid" : ""}`}>
                <label className="ui-field__label" htmlFor="otp">
                  6-digit code
                </label>
                <OtpInput
                  id="otp"
                  value={OTP}
                  onChange={handleOTPChange}
                  invalid={OTPValidation}
                  describedBy={OTPValidation ? "otp-error" : undefined}
                />
                {OTPValidation && (
                  <p className="ui-field__error" id="otp-error" role="alert">
                    <i className="fa fa-circle-exclamation" aria-hidden="true"></i>
                    Enter all 6 digits.
                  </p>
                )}
              </div>

              {/* THE RAIL AGAIN — same bar, same meaning: a span running out. */}
              {!showNewOTP && (
                <div className={`lg__timer ${timerTone}`}>
                  <div className="lg__timer-row">
                    <span>Code expires in</span>
                    <span className="lg__timer-value" role="timer" aria-live="polite">
                      {timeLeft}
                    </span>
                  </div>
                  <div className="lg__timer-track">
                    <div
                      className="lg__timer-fill"
                      style={{ width: `${countdownPercent}%` }}
                    ></div>
                  </div>
                </div>
              )}

              {showNewOTP && (
                <p className="lg__notice" role="alert">
                  <i className="fa fa-circle-exclamation" aria-hidden="true"></i>
                  That code has expired. Send a new one to carry on.
                </p>
              )}

              {!showNewOTP && (
                <button
                  className="ui-btn ui-btn--primary ui-btn--block"
                  type="submit"
                  disabled={isLoading}
                >
                  {isLoading && (
                    <span className="ui-btn__spinner" aria-hidden="true"></span>
                  )}
                  {isLoading ? "Signing in..." : "Sign in"}
                </button>
              )}
              {showNewOTP && (
                <button
                  className="ui-btn ui-btn--primary ui-btn--block"
                  type="button"
                  onClick={handleGenerateOTP}
                >
                  Send a new code
                </button>
              )}
            </form>
          )}

          <p className="lg__foot">
            <i className="fa fa-shield-halved" aria-hidden="true"></i>
            Signing in is by one-time code. We never ask for a password.
          </p>
        </div>
      </div>
    </div>
  );
};

export default Login;
